import {createTheme} from "@mui/material/styles";

export default function createAppTheme(isDarkMode = true) {
    const mode = isDarkMode ? 'dark' : 'light';
    const rowHover = isDarkMode ? "rgb(55, 69, 82)" : "#edf4f8";
    const input = isDarkMode ? "rgb(38, 53, 67)" : "#eef2f6";
    const darkBlue = isDarkMode ? "rgb(6, 13, 19)" : "#ffffff";
    const tableOdd = isDarkMode ? "rgb(14,24,32)" : "#f8fafc";
    const tableSelectedRow = isDarkMode ? "#1d304a" : "#e0f2ee";
    const dividerBorder = isDarkMode ? "rgb(38, 45, 52)" : "#dce3eb";
    const mainBackground = isDarkMode ? "rgb(18, 29, 39)" : "#ffffff";
    const dropdownBackground = isDarkMode ? input : mainBackground;
    const canvasBackground = isDarkMode ? mainBackground : "#f4f7fb";
    const tableHeaderBackground = isDarkMode ? "rgb(6, 13, 19)" : "#eef3f8";
    const textPrimary = isDarkMode ? "rgba(255, 255, 255, 0.87)" : "#172b4d";
    const textSecondary = isDarkMode ? "rgba(195, 195, 195, 1)" : "#52647c";
    const textMuted = isDarkMode ? "rgb(138, 147, 159)" : "#65758b";
    const primaryMain = isDarkMode ? "rgba(68, 199, 103, 1)" : "#087f63";
    const navbarHover = isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(23, 43, 77, 0.06)";
    const disabledDark = isDarkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(23, 43, 77, 0.38)";
    const activeColor = isDarkMode ? navbarHover : "#e0f2ee";
    const tableSummaryRowBackground = isDarkMode ? "rgb(6, 13, 19)" : "#eef3f7";
    const gridColors = {
        "--rdg-color-scheme": mode,
        "--rdg-color": textPrimary,
        "--rdg-border-color": dividerBorder,
        "--rdg-summary-border-color": dividerBorder,
        "--rdg-background-color": mainBackground,
        "--rdg-header-background-color": tableHeaderBackground,
        "--rdg-header-draggable-background-color": tableHeaderBackground,
        "--rdg-row-hover-background-color": rowHover,
        "--rdg-row-selected-background-color": tableSelectedRow,
        "--rdg-row-selected-hover-background-color": rowHover,
        "--rdg-selection-color": primaryMain,
        "--rdg-checkbox-color": primaryMain,
        "--rdg-checkbox-focus-color": dividerBorder,
        "--rdg-checkbox-disabled-border-color": dividerBorder,
        "--rdg-checkbox-disabled-background-color": dividerBorder
    };

    return createTheme({
        shadows: Array(25).fill('none'),
        palette: {
            mode,
            divider: dividerBorder,
            common: {
                black: "#000",
                white: "rgba(255, 255, 255, 0.87)"
            },
            background: {
                paper: mainBackground,
                default: canvasBackground,
                hover: isDarkMode ? "rgba(6, 13, 19, 0.7)" : "#f4f7fb",
                defaultBlue: canvasBackground,
                darkBlue: darkBlue,
                input: input,
                dropdown: dropdownBackground,
                rowHover: isDarkMode ? "rgba(77, 131, 225, 0.1)" : "#edf4f8",
                tableOdd: tableOdd,
                tableRowHover: rowHover,
                tableBorder: dividerBorder,
                tableHeader: tableHeaderBackground,
                tableSelectedRow: tableSelectedRow,
                navbarHover: navbarHover,
                activeColor: activeColor
            },
            border: {
                icon: dividerBorder,
                button: dividerBorder
            },
            primary: {
                light: isDarkMode ? "rgba(106, 206, 133, 1)" : "#159777",
                main: primaryMain,
                dark: isDarkMode ? "rgba(59, 172, 89, 1)" : "#06634d",
                contrastText: "rgba(245, 245, 245, 1)",
                white: textPrimary
            },
            secondary: {
                light: isDarkMode ? "rgba(35,140,246, 1)" : "#4d83ec",
                main: isDarkMode ? "rgba(0, 119, 246, 1)" : "#2563eb",
                dark: isDarkMode ? "rgba(0, 80, 175, 1)" : "#1d4ed8",
                contrastText: "#fff"
            },
            error: {
                light: isDarkMode ? "#e57373" : "#c93636",
                main: isDarkMode ? "#f44336" : "#c62828",
                dark: "#d32f2f",
                contrastText: "#fff"
            },
            text: {
                primary: textPrimary,
                secondary: textSecondary,
                disabled: disabledDark,
                disabledLight: textMuted,
                disabledDark,
                hint: textMuted,
                tableHeader: textSecondary,
                darkBlue: "rgb(18, 29, 39)",
                noticeDark: textSecondary,
            },
            action: {
                selected: activeColor
            },
            icon: {
                yellow: "rgb(255,202,67)"
            }
        },
        divider: {
            main: dividerBorder,
        },
        toolTipHelp: {
            marginLeft: 6,
            fontSize: 18,
            position: "relative",
            cursor: "pointer"
        },
        typography: {
            fontFamily: `'Rubik', sans-serif !important`
        },
        defaultDataGrid: {
            ...gridColors,
            "& [role=gridcell], & [role=columnheader]": {
                boxShadow: "none"
            },
            height: "100%",
            backgroundColor: mainBackground,
            border: "none",
            userSelect: "auto",
            "& div.rdg-header-row": {
                userSelect: "none",
                color: textSecondary,
                fontWeight: 600,
                fontSize: 12,
                "& ::after": {
                    width: 8,
                    cursor: "col-resize",
                    backgroundColor: dividerBorder,
                    position: "absolute",
                    right: 0
                },
                "& .rdg-cell": {
                    borderRight: "1px solid " + dividerBorder,
                    "&:nth-child(1),:nth-child(2)": {
                        borderRight: 0
                    }
                },
                "& .rdg-cell-resizable .rdg-sort-arrow": {
                    marginRight: 8,
                    marginLeft: 8,
                },
                "& .rdg-cell-resizable > div:nth-child(2)": {
                    width: "10px",
                    "&:hover": {
                        borderRight: "10px solid " + dividerBorder,
                    }
                },
                "& .rdg-cell-drag-over": {
                    backgroundColor: primaryMain
                }
            },
            "& div.rdg-checkbox-label": {
                height: 34,
            },
            "& div.rdg-cell": {
                boxShadow: "none",
                border: "none",
                borderBottom: "1px solid " + dividerBorder,
                padding: "0 8px",
                "&[aria-selected=true]": {
                    outline: 0
                }
            },
            "& div.rdg-row.rdg-row-even": {
                "&:hover": {
                    cursor: "pointer"
                }
            },
            "& div.rdg-row.rdg-row-odd": {
                backgroundColor: tableOdd,
                "&:hover": {
                    backgroundColor: rowHover,
                    cursor: "pointer"
                },
                "&[aria-selected=true]": {
                    backgroundColor: tableSelectedRow,
                    "&:hover": {
                        backgroundColor: rowHover,
                    }
                }
            },
            "& div.rdg-row.rdg-summary-row": {
                backgroundColor: tableSummaryRowBackground
            }
        },
        clicksReportDataGrid: {
            ...gridColors,
            "& [role=gridcell], & [role=columnheader]": {
                boxShadow: "none"
            },
            height: "100%",
            backgroundColor: mainBackground,
            border: "none",
            userSelect: "auto",
            "& div.rdg-header-row": {
                userSelect: "none",
                color: textSecondary,
                fontWeight: 600,
                fontSize: 12,
                "& ::after": {
                    width: 8,
                    cursor: "col-resize",
                    backgroundColor: dividerBorder,
                    position: "absolute",
                    right: 0
                },
                "& .rdg-cell-resizable .rdg-sort-arrow": {
                    marginRight: 8,
                    marginLeft: 8,
                },
                "& .rdg-cell-resizable > div": {
                    width: "10px",
                    borderRight: "1px solid " + dividerBorder,
                    "&:hover": {
                        borderRight: "10px solid " + dividerBorder,
                    }
                },
                "& .rdg-cell-drag-over": {
                    backgroundColor: primaryMain
                }
            },
            "& div.rdg-cell": {
                boxShadow: "none",
                border: "none",
                borderBottom: "1px solid " + dividerBorder,
                padding: "0 8px",
                "&[aria-selected=true]": {
                    outline: 0
                }
            },
            "& div.rdg-row.rdg-row-even": {
                "&:hover": {
                    cursor: "pointer"
                }
            },
            "& div.rdg-row.rdg-row-odd": {
                backgroundColor: tableOdd,
                "&:hover": {
                    backgroundColor: rowHover,
                    cursor: "pointer"
                },
                "&[aria-selected=true]": {
                    backgroundColor: tableSelectedRow,
                    "&:hover": {
                        backgroundColor: rowHover,
                    }
                }
            }
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    ":root": {
                        colorScheme: mode,
                        "--app-background": canvasBackground,
                        "--app-text": textPrimary,
                        "--app-input": input,
                        "--app-scrollbar": isDarkMode ? navbarHover : "#c3cfdd",
                        "--calendar-selcted": primaryMain,
                    },
                    "@media (max-width: 599.95px), (hover: none) and (pointer: coarse)": {
                        "html body .MuiInputBase-root .MuiInputBase-input": {
                            // Prevent Safari focus zoom, including compact modal and Autocomplete overrides.
                            fontSize: "16px !important"
                        }
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&:hover': {
                            borderColor: dividerBorder + " !important",
                        },
                    },
                    input: {
                        backgroundColor: input,
                        borderWidth: 1,
                        borderRadius: 4,
                        "&::placeholder": {
                            fontSize: 13
                        },
                        fontSize: 16
                    },
                    notchedOutline: {
                        borderWidth: 0
                    },
                    adornedStart: {
                        backgroundColor: input,
                    },
                    adornedEnd: {
                        backgroundColor: input
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderWidth: 0
                            },
                            '&:hover fieldset': {
                                borderWidth: 0
                            },
                            '&.Mui-focused fieldset': {
                                borderWidth: 1
                            },
                            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 1
                            }
                        },
                    }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    body1: {
                        fontSize: 14,
                        color: textPrimary,
                        fontWeight: 500,
                        whiteSpace: "nowrap"
                    }
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontWeight: 500,
                        fontSize: 20,
                        marginTop: 12
                    }
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: "16px 22px"
                    }
                },
            },
            MuiAlert: {
                styleOverrides: {
                    filledInfo: {
                        backgroundColor: "rgba(50, 112, 220, 1)"
                    },
                    filledWarning: {
                        color: "#172b4d"
                    },
                    filledError: {
                        color: "#fff"
                    },
                    filledSuccess: {
                        backgroundColor: primaryMain,
                    }
                },
            },
            MuiPaper: {
                defaultProps: {
                    elevation: 0
                },
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                        backgroundImage: 'none'
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    head: {
                        backgroundColor: tableHeaderBackground,
                        "&:hover": {
                            backgroundColor: tableHeaderBackground + " !important"
                        }
                    },
                    root: {
                        "&:hover": {
                            backgroundColor: rowHover + " !important"
                        }
                    }
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderBottom: "1px solid " + dividerBorder,
                        fontSize: 14,
                    },
                    head: {
                        backgroundColor: tableHeaderBackground,
                        color: textSecondary,
                        fontSize: 12,
                        fontWeight: 600,
                        borderLeft: 0,
                        borderBottom: "1px solid " + dividerBorder,
                    },
                    stickyHeader: {
                        backgroundColor: tableHeaderBackground
                    }
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        textTransform: "capitalize",
                        fontSize: 13,
                    },
                    outlinedInherit: {
                        borderColor: dividerBorder,
                        padding: "6px 16px",
                        "&.Mui-disabled": {
                            borderColor: dividerBorder,
                            color: disabledDark,
                        }
                    },
                    containedInherit: {
                        color: isDarkMode ? darkBlue : textPrimary,
                    },
                    containedSecondary: {
                        color: "#fff",
                        "&.Mui-disabled": {
                            color: disabledDark,
                        }
                    },
                    containedPrimary: {
                        "&.Mui-disabled": {
                            color: disabledDark,
                        }
                    }
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    inputRoot: {
                        backgroundColor: input
                    },
                    paper: {
                        backgroundColor: dropdownBackground,
                        border: `1px solid ${dividerBorder}`,
                        borderRadius: 10,
                        paddingTop: 0,
                        marginTop: 0,
                        fontSize: 14
                    },
                    clearIndicator: {
                        color: textPrimary
                    },
                    popupIndicator: {
                        color: textPrimary
                    }
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        margin: 0,
                        padding: "2px",
                        marginLeft: "3px",
                        "&&.MuiAutocomplete-tag": {
                            margin: 0,
                            marginLeft: "3px"
                        }
                    },
                    filledPrimary: {
                        color: isDarkMode ? darkBlue : "#fff"
                    },
                },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.8)" : "rgba(23, 43, 77, 0.35)"
                    }
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        backgroundColor: dividerBorder
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: darkBlue,
                        color: textPrimary,
                    }
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        color: textPrimary
                    }
                }
            },
            MuiSwitch: {
                styleOverrides: {
                    track: {
                        backgroundColor: textPrimary
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        "&.Mui-disabled": {
                            color: disabledDark,
                        }
                    },
                    colorInherit: {
                        color: textPrimary,
                        "&:hover": {
                            backgroundColor: navbarHover
                        }
                    },
                }
            },
            MuiModal: {
                styleOverrides: {
                    backdrop: {
                        opacity: "0.3 !important"
                    }
                }
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        backgroundColor: dropdownBackground,
                        border: `1px solid ${dividerBorder}`,
                        borderRadius: 10,
                    },
                    root: {
                        "& .MuiBackdrop-root": {
                            backgroundColor: "transparent !important",
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    icon: ({theme}) => ({
                        color: theme.palette.text.primary,
                    }),
                },
            },
        }
    });
}
